/**
=========================================================
* Trust Cargo - Messages
=========================================================

* Admins see every message and can add, edit and delete; a customer sees only
* their own, read-only. Adding and editing both happen in a dialog over this
* list rather than on a page of their own.
*/

import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import { Table, TableCell, TableHead, TableBody, TableRow } from "@mui/material";
import axios from "axios";

// Soft UI Dashboard React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import { FilterSelect, optionsFromRows, matches } from "components/FilterBar/controls";
import Pagination, { usePagination } from "components/Pagination";
import IdCell, { IdHead } from "components/IdCell";
import AddDialog from "components/AddDialog";
import RowActions from "components/RowActions";

// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api";

/**
 * Bodies are stored as HTML from the editor. Rendering the tags raw in a
 * table cell shows "<p>text</p>" to the user, so they are stripped for the
 * list - the dialog still edits the real value.
 */
const plain = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

function Messages() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [customer, setCustomer] = useState("");

  const isAdmin = sessionStorage.getItem("role_id") === "admin";

  const fetchData = async () => {
    try {
      const customerId = sessionStorage.getItem("customer_id");
      const response = await fetch(
        isAdmin ? `${API}/messages` : `${API}/customer_messages/${customerId}`
      );
      const data = await response.json();
      setRows(data.messages || []);
    } catch (e) {
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, []);

  const filteredRows = filterRows(
    rows.filter((r) => matches(r.customer_name || r.customer_id, customer)),
    query
  );
  const pager = usePagination(filteredRows);

  const removeOne = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this message?")) return;

    try {
      await axios.post(
        `${API}/delete_message`,
        { id: row.id },
        { headers: { Accept: "application/json" } }
      );
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete");
    }
  };

  const cell = { fontSize: 14 };

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <SuiBox mb={3}>
          <Card>
            <FilterBar
              title="Messages"
              value={query}
              onChange={setQuery}
              count={filteredRows.length}
              total={rows.length}
              placeholder="Search messages..."
              actions={
                isAdmin ? (
                  <Button variant="contained" color="primary" onClick={() => setAdding(true)}>
                    Add Message
                  </Button>
                ) : null
              }
            >
              <FilterSelect
                label="Customer"
                icon="person"
                value={customer}
                onChange={setCustomer}
                options={optionsFromRows(
                  rows.map((r) => ({ c: r.customer_name || r.customer_id })),
                  "c"
                )}
              />
            </FilterBar>

            {loading ? (
              <CargoLoader label="Loading messages" />
            ) : (
              <>
                <SuiBox sx={{ overflowX: "auto" }}>
                  <Table sx={{ minWidth: 800 }} aria-label="messages table">
                    <TableHead>
                      <TableRow>
                        <IdHead />
                        <TableCell align="center">Message</TableCell>
                        <TableCell align="center">Customer</TableCell>
                        {isAdmin && <TableCell align="center">Operations</TableCell>}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {pager.pageRows.map((row) => (
                        <TableRow key={row.id}>
                          <IdCell value={row.id} />
                          <TableCell align="center" sx={cell}>
                            {plain(row.body) || "-"}
                          </TableCell>
                          <TableCell align="center" sx={cell}>
                            {row.customer_name || row.customer_id || "-"}
                          </TableCell>

                          {isAdmin && (
                            <TableCell align="center" sx={cell}>
                              <RowActions
                                actions={[
                                  {
                                    icon: "edit",
                                    label: "Edit message",
                                    onClick: () => setEditingId(row.id),
                                  },
                                  {
                                    icon: "delete",
                                    label: "Delete",
                                    tone: "danger",
                                    onClick: () => removeOne(row),
                                  },
                                ]}
                              />
                            </TableCell>
                          )}
                        </TableRow>
                      ))}

                      {filteredRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={isAdmin ? 4 : 3} align="center" sx={{ py: 4 }}>
                            <SuiTypography variant="button" color="text">
                              No messages
                            </SuiTypography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </SuiBox>
                <Pagination {...pager} />
              </>
            )}
          </Card>
        </SuiBox>
      </SuiBox>

      <AddDialog
        open={adding}
        entity="message"
        onClose={() => setAdding(false)}
        onCreated={fetchData}
      />

      {/* same dialog, loaded with the existing message */}
      <AddDialog
        open={Boolean(editingId)}
        entity="message"
        recordId={editingId}
        onClose={() => setEditingId(null)}
        onCreated={fetchData}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Messages;
